import { Box, Flex, FormErrorMessage, Stack, Text } from '@chakra-ui/core'
import React, { useCallback, useMemo, useState } from 'react'
import { Folder, LayoutList, Palette, Tag, Users } from 'lucide-react'
import { InputWithIcon } from '../commonUI/InputWithIcon'
import { Controller, useFormContext } from 'react-hook-form'
import { ITag } from 'constants/interfaces'
import useLocaleText from 'components/useLocaleText'
import OptimizedGroupsTree from 'components/CommonComponents/OptimizedGroupsTree'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { SelectionMode } from 'components/CommonComponents/OptimizedGroupsTree/hooks/useSelection'
import UsersList from 'components/CommonComponents/UsersList'
import useWindowDimensions from 'hooks/useWindowDimensions'
import { PreviewAvatarsList } from 'components/CommonComponents/PreviewList'
import { StyledFormControl, StyledFormLabel } from '../commonUI/styles'
import { ColorPicker } from 'components/CommonComponents/ColorPicker/ColorPicker'
import { NavButtonTabs, TabKey } from 'components/CommonComponents/AudienceTabs'

interface TagFormProps {
  isLoading: boolean
}

export const TagForm: React.FC<TagFormProps> = ({ isLoading }) => {
  const { setValue, register, errors, control, getValues, watch } = useFormContext<ITag>()
  const [activeTab, setActiveTab] = useState<TabKey>('groups')
  const { windowHeight } = useWindowDimensions()

  const listHeight = windowHeight * 0.3

  const { retailUsersObject, groups: groupsObject } = useSelector((state: RootState) => state.config.config)
  const { groups } = useSelector((state: RootState) => state.config.config)

  const tag_name = useLocaleText('tag_name')
  const tag_color = useLocaleText('pick_color')
  const t_users = useLocaleText('users')
  const t_groups = useLocaleText('groups')
  const audience = useLocaleText('participants')

  const watchedGroups = watch('groups')
  const watchedUsers = watch('users')

  const selectedGroupIds = useMemo(() => watchedGroups || [], [watchedGroups])
  const selectedUsersIds = useMemo(() => watchedUsers || [], [watchedUsers])
  const tagColor = useMemo(() => getValues('tag_color') || '', [getValues])

  const tagGroups = useMemo(() => {
    return selectedGroupIds.map((id) => groupsObject[id])
  }, [groupsObject, selectedGroupIds])

  const tagUsers = useMemo(() => {
    return selectedUsersIds.map((id) => retailUsersObject?.[id])
  }, [retailUsersObject, selectedUsersIds])

  const handleUsersSelectionChange = useCallback(
    (users: string[]) => {
      setValue('users', users)
    },
    [setValue]
  )

  const handleGroupsSelectionChange = useCallback(
    (groups: string[]) => {
      setValue('groups', groups)
    },
    [setValue]
  )

  const handleColorChange = useCallback(
    (color: string) => {
      setValue('tag_color', color)
    },
    [setValue]
  )

  return (
    <Stack spacing={8} pointerEvents={isLoading ? 'none' : 'auto'} opacity={isLoading ? 0.8 : 1}>
      <Flex alignItems="center" style={{ gap: '10px' }} w="100%">
        <StyledFormControl isInvalid={!!errors.name} style={{ width: '50%' }}>
          <StyledFormLabel required>
            <Tag size={18} strokeWidth={2.5} />
            <Text>{tag_name}</Text>
          </StyledFormLabel>
          <InputWithIcon
            w="100%"
            name="name"
            ref={register({
              required: 'Tag name is required',
            })}
            placeholder={tag_name}
            autoComplete="tag-name"
          />
          <FormErrorMessage>{errors.name && errors.name.message}</FormErrorMessage>
        </StyledFormControl>

        <StyledFormControl style={{ width: '50%' }}>
          <StyledFormLabel>
            <Palette size={18} strokeWidth={2.5} />
            <Text>{tag_color}</Text>
          </StyledFormLabel>
          <Controller
            control={control}
            name="tag_color"
            defaultValue={tagColor}
            as={ColorPicker}
            color={tagColor}
            onChange={([color]) => {
              handleColorChange(color)
              return color
            }}
          />
        </StyledFormControl>
      </Flex>

      <StyledFormControl>
        <StyledFormLabel>
          <LayoutList size={18} strokeWidth={2.5} />
          <Text>{audience}</Text>
        </StyledFormLabel>

        <Flex width="100%" justifyContent="center">
          <NavButtonTabs
            value={activeTab}
            onChange={setActiveTab}
            tabs={[
              { key: 'groups', label: t_groups },
              { key: 'users', label: t_users },
            ]}
          />
        </Flex>

        <StyledFormControl mt="10px">
          <PreviewAvatarsList
            icon={Users}
            label={t_users}
            items={tagUsers.map((user) => ({
              name: `${user?.first_name} ${user?.last_name || ''}`,
              profile_img_url: user?.profile_img_url,
            }))}
            defaultValue="-"
            max={10}
          />

          <PreviewAvatarsList
            icon={Folder}
            label={t_groups}
            items={tagGroups.map((group) => ({
              name: group?.name || '',
            }))}
            defaultValue="-"
            max={10}
            isGroup
          />
        </StyledFormControl>
        <Box borderRadius="10px" padding="10px" mt={2} border="1px solid #e0e0e0">
          <Box display={activeTab === 'groups' ? 'block' : 'none'}>
            <Controller
              control={control}
              name="groups"
              defaultValue={selectedGroupIds}
              as={
                <OptimizedGroupsTree
                  groups={groups}
                  allowedGroups={Object.keys(groups)}
                  showSearch
                  listHeight={listHeight}
                  initialSelectedIds={selectedGroupIds}
                  onSelectionChange={handleGroupsSelectionChange}
                  selectionMode={SelectionMode.RAW}
                  isShowTags
                />
              }
            />
          </Box>
          <Box display={activeTab === 'users' ? 'block' : 'none'}>
            <Controller
              control={control}
              name="users"
              defaultValue={selectedUsersIds}
              as={
                <UsersList
                  showSearch
                  isShowTags
                  listHeight={listHeight}
                  initialSelectedIds={selectedUsersIds}
                  selectionMode={SelectionMode.RAW}
                  onSelectionChange={handleUsersSelectionChange}
                />
              }
            />
          </Box>
        </Box>
      </StyledFormControl>
      <Controller control={control} name="tag_type" defaultValue={0} as={<Box style={{ display: 'none' }} />} />
    </Stack>
  )
}
